// The questionnaire DEFINITION (v3, 2026-11-03) — data only, the wizard UI is
// screens/Questionnaire.jsx. v3 widened v2 (school, sleep, siblings, life events,
// the child's world, 18 separate fears) and shows each parent only what fits:
//   • `ages: [lo, hi]` — shown only for a child of that age (inclusive)
//   • `when(answers)` — shown only when an earlier answer makes it relevant
//   • `optional: true` — «اختیاری», never blocks «بعدی»
// IDs: an item that kept its v2 meaning keeps its v2 id (q9, q15, q16 …) so the
// games, tales, seasons and admin keep reading it; a merged item keeps the more
// central id (q1→q15, q2→q11, q5→q12, q3→q22, q4→q25, q35→q32). New items have
// named ids. Old answer sets are upgraded server-side (services/questionnaire_v3.py).
// ⚠️ Every concern label here must exist in backend services/questionnaire_v3.py
// (CONCERN_LABELS) — tests/test_questionnaire_v3.py fails when one is missing.
export const Q_VERSION = 3;

const SCHOOL = "دبستان";
export const isSchoolChild = (a) => a.sch_where === SCHOOL;
const hasSiblings = (a) => a.fam_sibs && a.fam_sibs !== "ندارد";
const notAlone = (a) => a.slp_where && a.slp_where !== "اتاق خودش، تنها";

// Life events: one id per event, `concern` = how it reads as a story topic when
// the parent marks its effect متوسط or زیاد.
export const LIFE_EVENT_GROUPS = [
  { title: "خانواده", items: [
    { id: "ev_newsib", label: "تولد خواهر یا برادر", concern: "آمدن خواهر یا برادر تازه" },
    { id: "ev_pregnant", label: "مادر باردار است", concern: "منتظرِ خواهر یا برادر تازه" },
    { id: "ev_divorce", label: "جدایی یا طلاق والدین", concern: "کنار آمدن با جدایی والدین" },
    { id: "ev_conflict", label: "اختلاف شدید و مداوم والدین", concern: "تنش میان والدین" },
    { id: "ev_remarry", label: "ازدواج دوبارهٔ پدر یا مادر", concern: "عضو تازه در خانواده" },
    { id: "ev_grandparent", label: "آمدن یا رفتن مادربزرگ/پدربزرگ به خانه", concern: "تغییر در آدم‌های خانه" },
    { id: "ev_away", label: "دوری طولانی پدر یا مادر (کار در شهر دیگر، مأموریت، سربازی)", concern: "دلتنگیِ پدر یا مادرِ دور" },
    { id: "ev_emigrate", label: "مهاجرت یکی از والدین یا نزدیکان", concern: "دوری از عزیزِ مهاجر" },
    { id: "ev_plan_move", label: "برنامهٔ مهاجرت خانواده", concern: "آماده‌شدن برای مهاجرت" },
  ] },
  { title: "از دست دادن", items: [
    { id: "ev_loss_close", label: "درگذشت پدر، مادر، خواهر یا برادر", concern: "سوگِ یک عزیزِ نزدیک" },
    { id: "ev_loss_relative", label: "درگذشت مادربزرگ، پدربزرگ یا خویشاوند", concern: "سوگِ یک خویشاوند" },
    { id: "ev_loss_pet", label: "درگذشت یا گم شدن حیوان خانگی", concern: "از دست دادنِ حیوان خانگی" },
    { id: "ev_loss_friend", label: "دور شدن از دوست صمیمی یا مراقب محبوب (پرستار، مربی)", concern: "دوری از دوست یا مراقب محبوب" },
  ] },
  { title: "سلامت", items: [
    { id: "ev_ill_child", label: "بیماری جدی یا جراحی خود کودک", concern: "کنار آمدن با بیماری یا جراحی" },
    { id: "ev_hospital", label: "بستری شدن کودک", concern: "تجربهٔ بیمارستان" },
    { id: "ev_ill_family", label: "بیماری جدی یکی از والدین یا نزدیکان", concern: "بیماریِ یک عزیز" },
    { id: "ev_parent_stress", label: "فشار روحی شدید یکی از والدین", concern: "خستگی و ناراحتیِ پدر یا مادر" },
  ] },
  { title: "خانه و مدرسه", items: [
    { id: "ev_move_home", label: "جابه‌جایی خانه", concern: "خانهٔ تازه" },
    { id: "ev_move_city", label: "جابه‌جایی شهر یا کشور", concern: "شهر یا کشورِ تازه" },
    { id: "ev_start_kg", label: "شروع مهدکودک", concern: "شروع مهدکودک" },
    { id: "ev_start_school", label: "شروع پیش‌دبستانی یا کلاس اول", concern: "شروع مدرسه" },
    { id: "ev_change_school", label: "تغییر مدرسه یا مهد", concern: "مدرسه یا مهدِ تازه" },
    { id: "ev_new_teacher", label: "تغییر معلم یا مربی", concern: "معلم یا مربیِ تازه" },
  ] },
  { title: "مرحله‌های رشد", items: [
    { id: "ev_potty", label: "گرفتن از پوشک", concern: "خداحافظی با پوشک" },
    { id: "ev_weaning", label: "گرفتن از شیر، پستانک یا شیشه", concern: "خداحافظی با پستانک یا شیشه" },
    { id: "ev_own_room", label: "جدا شدن اتاق یا تخت", concern: "اتاق یا تختِ خودش" },
    { id: "ev_sleep_alone", label: "شروع تنها خوابیدن", concern: "تنها خوابیدن" },
  ] },
  { title: "تجربه‌های ترسناک", items: [
    { id: "ev_war", label: "جنگ، حملهٔ هوایی، صدای انفجار یا آژیر", concern: "احساس امنیت پس از روزهای ترسناک" },
    { id: "ev_disaster", label: "زلزله، سیل یا آتش‌سوزی", concern: "احساس امنیت پس از حادثهٔ طبیعی" },
    { id: "ev_accident", label: "تصادف یا حادثه (برای خودش یا جلوی چشمش)", concern: "کنار آمدن با یک حادثه" },
    { id: "ev_scary_media", label: "دیدن صحنه یا خبر ترسناک", concern: "تصویرهای ترسناکی که دیده" },
    { id: "ev_animal_attack", label: "گاز گرفتن یا حملهٔ حیوان", concern: "ترس پس از حملهٔ حیوان" },
    { id: "ev_lost", label: "گم شدن در جای شلوغ", concern: "ترس پس از گم شدن" },
  ] },
  { title: "اقتصادی", items: [
    { id: "ev_money", label: "تغییر محسوس وضع مالی یا از دست رفتن شغل", concern: "تغییرِ وضع خانواده" },
  ] },
];
export const LIFE_EVENTS = LIFE_EVENT_GROUPS.flatMap(g => g.items);
export const IMPACT_OPTIONS = [
  { label: "کم", value: 1 }, { label: "متوسط", value: 2 }, { label: "زیاد", value: 3 },
];

// The fears, one per item (v2 had five lumped ones). q55/q56/q57/q58/q59 keep
// their v2 ids for the closest single fear.
export const FEAR_ITEMS = [
  { id: "q55", label: "تاریکی", concern: "ترس از تاریکی" },
  { id: "fear_alone", label: "تنها خوابیدن", concern: "ترس از تنها خوابیدن" },
  { id: "fear_monster", label: "لولو، هیولا یا موجودات خیالی", concern: "ترس از هیولا و موجودات خیالی" },
  { id: "fear_jinn", label: "جن و روح", concern: "ترس از جن و روح" },
  { id: "fear_dogcat", label: "سگ یا گربه", concern: "ترس از سگ یا گربه" },
  { id: "fear_insect", label: "حشرات", concern: "ترس از حشرات" },
  { id: "q56", label: "صداهای بلند (رعد، جاروبرقی، ترقه)", concern: "ترس از صداهای بلند" },
  { id: "fear_water", label: "آب، حمام یا شنا", concern: "ترس از آب و حمام" },
  { id: "fear_height", label: "ارتفاع", concern: "ترس از ارتفاع" },
  { id: "fear_doctor", label: "دکتر، دندان‌پزشک یا آمپول", concern: "ترس از دکتر و آمپول" },
  { id: "q57", label: "غریبه‌ها", concern: "ترس از غریبه‌ها" },
  { id: "fear_lost", label: "گم شدن", concern: "ترس از گم شدن" },
  { id: "q58", label: "دوری از شما (اضطراب جدایی)", concern: "اضطراب جدایی" },
  { id: "fear_parent_loss", label: "مرگ یا مریض شدن شما", concern: "ترس از مریض شدن یا از دست دادن والدین" },
  { id: "q59", label: "زخمی شدن یا افتادن", concern: "ترس از آسیب جسمانی" },
  { id: "fear_war", label: "جنگ، انفجار یا آژیر", concern: "ترس از جنگ و صدای انفجار" },
  { id: "fear_quake", label: "زلزله", concern: "ترس از زلزله" },
];

// fam_sib_rel chips that are a story topic of their own.
export const SIBLING_FLAGS = {
  "حسادت و رقابت": { id: "sib_jealous", label: "حسادت به خواهر/برادر" },
  "دعوای زیاد": { id: "sib_fight", label: "دعوا با خواهر/برادر" },
  "کوچک‌تر را اذیت می‌کند": { id: "sib_tease", label: "اذیت کردن خواهر/برادر کوچک‌تر" },
  "بزرگ‌تر اذیتش می‌کند": { id: "sib_bullied", label: "اذیت شدن از طرف خواهر/برادر بزرگ‌تر" },
};
export const SLEEP_ALONE_CONCERN = { id: "slp_alone", label: "یاد گرفتنِ تنها خوابیدن" };
// The rank's «nothing to work on» answer.
export const NO_PRIORITY = "مسئلهٔ خاصی نیست؛ فقط قصه‌های خوب";

const F = "frequency";
export const Q_SECTIONS = [
  {
    title: "دغدغهٔ شما", icon: "💬",
    desc: "با زبان خودتان بنویسید؛ این‌ها قصه‌ها را از همه چیز بیشتر شخصی می‌کنند.",
    questions: [
      { id: "open_concern", type: "text", label: "این روزها بزرگ‌ترین دغدغه‌تان دربارهٔ {name} چیست؟", maxLen: 500, required: false },
      { id: "open_hope", type: "text", label: "دوست دارید تا سه ماه دیگر چه تغییری در او ببینید؟", maxLen: 300, required: false },
      { id: "open_love", type: "text", label: "سه ویژگی‌ای که بیشتر از همه در او دوست دارید؟", maxLen: 300, required: false },
    ],
  },
  {
    title: "دنیای {name}", icon: "🧸",
    desc: "علاقه‌ها و آدم‌های زندگی‌اش، تا قصه‌ها از دنیای خودش ساخته شوند.",
    questions: [
      { id: "int_topics", type: "multi", label: "به چه چیزهایی علاقه دارد؟",
        options: ["حیوانات", "ماشین و قطار و هواپیما", "فضا و ستاره‌ها", "دایناسورها", "دریا و ماهی‌ها", "جنگل و طبیعت", "نقاشی", "موسیقی و آواز", "فوتبال و ورزش", "ساختن و لگو", "آشپزی", "کتاب و قصه", "پری‌ها و جادو", "معما و آزمایش", "عروسک و خاله‌بازی"] },
      { id: "int_animal", type: "text", label: "حیوان محبوبش؟", maxLen: 60, required: false },
      { id: "q72", type: "text", label: "شخصیت‌های کارتونی یا کتابیِ محبوبش؟", maxLen: 300, required: false },
      { id: "int_people", type: "text", label: "آدم‌ها یا حیوان خانگی‌ای که دوست دارید در قصه‌ها بیایند؟", desc: "مثلاً «داداش آرش»، «مامان‌جون»، «گربه‌مان پیشی».", maxLen: 200, required: false },
      { id: "int_lovey", type: "text", label: "اسباب‌بازی یا شیئی که همیشه همراهش است؟", maxLen: 80, required: false },
      { id: "int_places", type: "multi", optional: true, label: "جاهایی که دوستشان دارد",
        options: ["خانهٔ مادربزرگ و پدربزرگ", "پارک", "دریا", "کوه و جنگل", "روستا", "حرم و مسجد", "کتابخانه", "باغ‌وحش", "شهربازی"] },
      { id: "int_calm", type: "multi", label: "چه چیزی آرامش می‌کند؟", exclusive: "نمی‌دانم",
        options: ["بغل و نوازش", "لالایی", "قصه", "عروسک یا پتوی خاص", "کمی تنها ماندن", "حرف زدن", "نفس عمیق", "نمی‌دانم"] },
      { id: "int_kind", type: "multi", max: 2, label: "چه جور قصه‌هایی را بیشتر دوست دارد؟ (حداکثر ۲)",
        options: ["ماجرایی", "خنده‌دار", "آرام و رؤیایی", "حیوانات سخن‌گو", "جادویی", "واقعی و روزمره"] },
    ],
  },
  {
    title: "خانواده و خانه", icon: "🏠",
    desc: "تا آدم‌ها و فضای قصه شبیه خانهٔ خودش باشد.",
    questions: [
      { id: "fam_lives", type: "multi", label: "با چه کسانی زندگی می‌کند؟",
        options: ["مادر", "پدر", "خواهر یا برادر", "مادربزرگ یا پدربزرگ", "سایر بستگان", "بین دو خانه رفت‌وآمد می‌کند"] },
      { id: "fam_carer", type: "single", optional: true, label: "در طول روز بیشتر پیش چه کسی است؟",
        options: ["مادر", "پدر", "هر دو به‌نوبت", "مادربزرگ یا پدربزرگ", "پرستار", "بیشتر روز در مهد یا مدرسه"] },
      { id: "fam_sibs", type: "single", label: "خواهر یا برادر دارد؟", options: ["ندارد", "یکی", "دو تا", "سه تا یا بیشتر"] },
      { id: "fam_order", type: "single", label: "فرزند چندم است؟", when: hasSiblings, options: ["اول", "وسطی", "آخر", "دوقلو یا چندقلو"] },
      { id: "fam_sib_rel", type: "multi", label: "رابطه‌اش با خواهر/برادر", when: hasSiblings,
        options: ["صمیمی و هم‌بازی", "حسادت و رقابت", "دعوای زیاد", "کوچک‌تر را اذیت می‌کند", "بزرگ‌تر اذیتش می‌کند", "رابطهٔ کمی دارند"] },
      { id: "q62", type: "single", label: "حال‌وهوای خانه بیشتر وقت‌ها چطور است؟", options: ["آرام و باثبات", "گاهی پرتنش", "اغلب پرتنش یا پرتعارض"] },
      { id: "q63", type: "single", label: "آیا شاهد دعوا یا تنش شدید بین بزرگ‌ترهای خانه بوده است؟", options: ["خیر", "بله، به‌ندرت", "بله، گاهی", "بله، مکرراً"] },
      { id: "fam_faith", type: "single", label: "دین در زندگی روزمرهٔ خانواده چه جایی دارد؟", desc: "عمق فضای مذهبیِ قصه‌ها را با این تنظیم می‌کنیم.",
        options: ["بسیار پررنگ", "معمولی", "بیشتر فرهنگی و مناسبتی"] },
    ],
  },
  {
    title: "اتفاق‌های زندگی", icon: "🌦️",
    desc: "اتفاق‌های بزرگ رفتار و خواب بچه‌ها را عوض می‌کنند؛ قصه می‌تواند کمکشان کند.",
    questions: [
      { id: "life_events", type: "lifeevents", label: "در دو سال اخیر، یا همین حالا، کدام‌یک از این‌ها در زندگی {name} پیش آمده؟",
        desc: "برای هر مورد بگویید هنوز ادامه دارد یا نه، و چقدر رویش اثر گذاشته." },
      { id: "life_other", type: "text", label: "اتفاق دیگری که در فهرست نبود؟", maxLen: 300, required: false },
    ],
  },
  {
    title: "احساس‌ها و خلق‌وخو", icon: "💛",
    desc: "هر مورد را برای ۶ ماه اخیر جواب دهید.",
    questions: [
      { id: "q15", type: F, concern: "عصبانیت و ازدست‌دادن کنترل", label: "زود عصبانی می‌شود و از کوره در می‌رود." },
      { id: "emo_tantrum", type: F, ages: [3, 6], concern: "قشقرق و جیغ‌زدن", label: "قشقرق راه می‌اندازد (جیغ می‌زند، خودش را زمین می‌اندازد)." },
      { id: "q6", type: F, positive: true, label: "بعد از ناراحتی می‌تواند کم‌کم خودش را آرام کند." },
      { id: "emo_express", type: F, positive: true, label: "می‌تواند احساسش را با کلمه بگوید («ناراحتم چون…»)." },
      { id: "q9", type: F, concern: "نگرانی و افکار ناراحت‌کننده", label: "زیاد نگران است و دلداری زیادی لازم دارد." },
      { id: "q10", type: F, concern: "غم و دلمردگی", label: "بیشتر وقت‌ها غمگین، بی‌حوصله یا زودرنج است." },
      { id: "emo_cry", type: F, optional: true, concern: "زودرنجی و گریهٔ زیاد", label: "با کوچک‌ترین ناراحتی گریه می‌کند و دیر آرام می‌شود." },
      { id: "q11", type: F, concern: "کم‌آوردن و «نمی‌تونم»", label: "تا کاری سخت می‌شود، می‌گوید «نمی‌تونم» و رها می‌کند." },
      { id: "emo_perfect", type: F, optional: true, concern: "ترس از اشتباه کردن", label: "از اشتباه کردن خیلی می‌ترسد یا اگر کارش بی‌نقص نشود به‌هم می‌ریزد." },
      { id: "emo_self", type: F, ages: [5, 10], concern: "حرف‌های منفی دربارهٔ خودش", label: "دربارهٔ خودش حرف منفی می‌زند («من بدم»، «هیچ‌کس دوستم نداره»)." },
      { id: "q12", type: F, concern: "کمرویی و کناره‌گیری", label: "در جمع یا با آدم‌های تازه کم‌رو است و دیر گرم می‌گیرد." },
      { id: "q13", type: F, concern: "نشانه‌های جسمانیِ استرس", label: "وقت نگرانی، دل‌درد یا سردرد می‌گیرد." },
      { id: "q44", type: F, concern: "حسادت به توجه دیگران", label: "وقتی دیگران (مثلاً خواهر یا برادر) توجه می‌گیرند، حسادت می‌کند." },
    ],
  },
  {
    title: "رفتار و قانون‌ها", icon: "🧭",
    desc: "هر مورد را برای ۶ ماه اخیر جواب دهید.",
    questions: [
      { id: "q16", type: F, concern: "بحث و مخالفت با قوانین", label: "با خواسته‌ها و قانون‌های بزرگ‌ترها بحث و مخالفت می‌کند." },
      { id: "q17", type: F, concern: "نافرمانی", label: "حتی با چند بار یادآوری، کاری را که از او خواسته‌اند انجام نمی‌دهد." },
      { id: "q18", type: F, concern: "نپذیرفتن مسئولیت", label: "اشتباهش را گردن دیگران می‌اندازد." },
      { id: "q19", type: F, concern: "پرخاشگری فیزیکی", label: "وقتی عصبانی است می‌زند، گاز می‌گیرد یا وسایل را پرت می‌کند." },
      { id: "beh_words", type: F, concern: "حرف زشت و بی‌ادبانه", label: "حرف زشت یا بی‌ادبانه می‌زند." },
      { id: "beh_manners", type: F, optional: true, concern: "بی‌احترامی به بزرگ‌ترها", label: "به بزرگ‌ترها بی‌احترامی می‌کند." },
      { id: "q20", type: F, ages: [6, 10], optional: true, concern: "کینه و تلافی", label: "کینه به دل می‌گیرد یا دنبال تلافی است." },
      { id: "q21", type: F, ages: [4, 10], concern: "دروغ گفتن", label: "دروغ می‌گوید." },
      { id: "beh_take", type: F, concern: "برداشتن وسایل دیگران", label: "وسایل دیگران را بی‌اجازه برمی‌دارد." },
      { id: "beh_whine", type: F, concern: "نق زدن و اصرار", label: "برای رسیدن به خواسته‌اش نق می‌زند و اصرار می‌کند." },
      { id: "q42", type: F, concern: "توقع برآورده‌شدن فوری خواسته‌ها", label: "انتظار دارد خواسته‌اش فوری برآورده شود." },
      { id: "beh_buy", type: F, optional: true, concern: "پافشاری برای خرید", label: "در فروشگاه یا مهمانی برای خرید اسباب‌بازی یا خوراکی پافشاری می‌کند." },
      { id: "q41", type: F, concern: "نپذیرفتن باخت", label: "وقتی نمی‌برد یا بهترین نیست، به‌هم می‌ریزد." },
      { id: "q40", type: F, ages: [5, 10], concern: "احساس برتری", label: "فکر می‌کند از بقیهٔ بچه‌ها بهتر است و این را می‌گوید." },
      { id: "q43", type: F, ages: [5, 10], optional: true, concern: "خودمحوری در گفت‌وگو", label: "بیشتر از خودش حرف می‌زند تا به دیگران گوش بدهد." },
    ],
  },
  {
    title: "توجه و انرژی", icon: "⚡",
    desc: "هر مورد را برای ۶ ماه اخیر جواب دهید.",
    questions: [
      { id: "q22", type: F, concern: "حواس‌پرتی و تمرکز کم", label: "حواسش زود پرت می‌شود و تمرکز روی یک کار برایش سخت است." },
      { id: "q23", type: F, concern: "بی‌قراری و پرتحرکی", label: "آرام نشستن برایش سخت است؛ مدام در حرکت است." },
      { id: "q24", type: F, concern: "تکانشگری", label: "پیش از فکر کردن حرف می‌زند یا کاری می‌کند." },
      { id: "q25", type: F, concern: "دشواری صبر کردن", label: "صبر کردن برای نوبتش، در بازی یا صف، برایش خیلی سخت است." },
      { id: "att_steps", type: F, ages: [5, 10], optional: true, concern: "فراموش کردن دستورهای چندمرحله‌ای", label: "دستور دومرحله‌ای («مسواک بزن، بعد لباس بپوش») را فراموش می‌کند." },
      { id: "att_lose", type: F, ages: [6, 10], optional: true, concern: "گم کردن وسایل و بی‌نظمی", label: "وسایلش را گم می‌کند یا خیلی نامرتب است." },
    ],
  },
  {
    title: "دوستی و بازی", icon: "🤝",
    desc: "هر مورد را برای ۶ ماه اخیر جواب دهید.",
    questions: [
      { id: "q26", type: F, ages: [5, 10], concern: "نداشتن دوست صمیمی", label: "دوست صمیمی ندارد یا دوست پیدا کردن برایش سخت است." },
      { id: "q27", type: F, concern: "دشواری ورود به بازی گروهی", label: "نمی‌داند چطور وارد بازی گروهی بچه‌ها شود." },
      { id: "q28", type: F, concern: "دشواری رعایت نوبت و سهیم‌شدن", label: "نوبت را رعایت نمی‌کند یا وسایلش را قسمت نمی‌کند." },
      { id: "q29", type: F, concern: "مورد اذیت یا طردشدن", label: "بچه‌های دیگر مسخره‌اش می‌کنند یا کنارش می‌گذارند." },
      { id: "q30", type: F, concern: "زورگویی به دیگران", label: "به بچه‌های دیگر زور می‌گوید یا مجبورشان می‌کند." },
      { id: "q31", type: F, ages: [4, 10], optional: true, concern: "ترجیح بازی انفرادی", label: "ترجیح می‌دهد بیشتر وقت‌ها تنها بازی کند." },
      { id: "_h_strengths", type: "header", label: "نقاط قوت", desc: "در این بخش، «اغلب» و «تقریباً همیشه» یعنی توانمندیِ بیشتر." },
      { id: "q32", type: F, positive: true, label: "مهربان است و ناراحتی دیگران را می‌فهمد." },
      { id: "q33", type: F, positive: true, label: "با میل خودش چیزهایش را با دیگران قسمت می‌کند." },
      { id: "q34", type: F, positive: true, label: "بدون اینکه از او بخواهند، کمک می‌کند." },
      { id: "q36", type: F, positive: true, label: "کارها و بازی‌هایش را تا آخر ادامه می‌دهد." },
    ],
  },
  {
    title: "مهد و مدرسه", icon: "🎒",
    desc: "یادگیری و فضای مهد یا مدرسه.",
    questions: [
      { id: "sch_where", type: "single", label: "الان کجا می‌رود؟", options: ["خانه است", "مهدکودک", "پیش‌دبستانی", SCHOOL] },
      { id: "q14", type: F, when: (a) => a.sch_where && a.sch_where !== "خانه است", concern: "مقاومت برای رفتن به مهد یا مدرسه", label: "صبح‌ها برای رفتن به مهد یا مدرسه گریه یا مقاومت می‌کند." },
      { id: "sch_teacher", type: F, optional: true, when: (a) => a.sch_where && a.sch_where !== "خانه است", concern: "ترس یا ناراحتی از معلم یا مربی", label: "از معلم یا مربی‌اش می‌ترسد یا از او شکایت دارد." },
      { id: "sch_homework", type: F, when: isSchoolChild, concern: "دعوا و تعلل سر تکلیف", label: "تکلیف نوشتن با دعوا، تعلل یا گریه همراه است." },
      { id: "sch_test", type: F, when: isSchoolChild, concern: "اضطراب امتحان", label: "قبل از امتحان یا پرسش کلاسی خیلی مضطرب است." },
      { id: "sch_behind", type: F, when: isSchoolChild, optional: true, label: "در خواندن، نوشتن یا حساب از هم‌کلاسی‌ها عقب‌تر است." },
      { id: "sch_bored", type: F, when: isSchoolChild, optional: true, concern: "بی‌علاقگی به درس", label: "به درس و یادگیری علاقه‌ای ندارد." },
      { id: "sch_curious", type: F, positive: true, optional: true, label: "کنجکاو است و زیاد سؤال می‌پرسد." },
      { id: "sch_report", type: "multi", optional: true, when: (a) => a.sch_where && a.sch_where !== "خانه است", exclusive: "چیزی نگفته",
        label: "معلم یا مربی دربارهٔ او چه گفته؟",
        options: ["چیزی نگفته", "کم‌دقتی", "پرخاشگری", "گوشه‌گیری", "بی‌نظمی", "پیشرفت خوب"] },
    ],
  },
  {
    title: "خواب، غذا و کارهای روزمره", icon: "🌛",
    desc: "خواب، غذا، عادت‌ها و صفحه‌نمایش.",
    questions: [
      { id: "slp_where", type: "single", label: "کجا می‌خوابد؟", options: ["اتاق خودش، تنها", "با خواهر یا برادر", "اتاق پدر و مادر", "تخت پدر و مادر"] },
      { id: "slp_goal", type: "yesno", label: "دوست دارید کم‌کم تنها خوابیدن را یاد بگیرد؟", when: notAlone },
      { id: "slp_time", type: "single", optional: true, label: "معمولاً چه ساعتی می‌خوابد؟", options: ["قبل از ۹", "۹ تا ۱۰", "۱۰ تا ۱۱", "بعد از ۱۱"] },
      { id: "q37", type: F, concern: "مقاومت وقت خواب", label: "وقت خواب مقاومت می‌کند و خوابیدنش طول می‌کشد." },
      { id: "slp_wake", type: F, concern: "بیدار شدن‌های شبانه", label: "شب بیدار می‌شود و سراغ شما می‌آید." },
      { id: "slp_dream", type: F, concern: "کابوس", label: "کابوس می‌بیند." },
      { id: "toi_night", type: F, ages: [5, 10], concern: "شب‌ادراری", label: "شب‌ها جایش را خیس می‌کند." },
      { id: "toi_day", type: F, ages: [3, 5], concern: "دشواری گرفتن از پوشک", label: "هنوز در گرفتن از پوشک یا رفتن به دستشویی مشکل دارد." },
      { id: "q38", type: F, concern: "سخت‌گیری در غذا", label: "در غذا خوردن خیلی سخت‌گیر است." },
      { id: "food_junk", type: F, optional: true, concern: "اصرار به خوراکی ناسالم", label: "به شیرینی و خوراکی‌های ناسالم اصرار دارد." },
      { id: "food_feed", type: F, optional: true, concern: "غذا خوردنِ طولانی", label: "غذا خوردنش خیلی طول می‌کشد یا باید به او غذا داد." },
      { id: "q39", type: F, optional: true, concern: "جویدن ناخن و عادت‌های عصبی", label: "ناخن می‌جود یا با مو ور می‌رود." },
      { id: "hab_suck", type: F, optional: true, concern: "مکیدن انگشت یا پستانک", label: "انگشت، پستانک یا شیشه می‌مکد." },
      { id: "hab_clean", type: F, optional: true, concern: "مقاومت در مسواک و حمام", label: "مسواک زدن یا حمام رفتن با مقاومت همراه است." },
      { id: "self_care", type: F, optional: true, concern: "وابستگی در کارهای شخصی", label: "کارهای شخصی‌اش (لباس پوشیدن، جمع کردن اسباب‌بازی) را فقط با اصرار انجام می‌دهد." },
      { id: "scr_hours", type: "single", label: "روزی چقدر با تبلت، موبایل یا تلویزیون می‌گذراند؟", options: ["کمتر از ۱ ساعت", "۱ تا ۲ ساعت", "۲ تا ۴ ساعت", "بیشتر از ۴ ساعت"] },
      { id: "q53", type: F, concern: "خشم هنگام پایان زمان صفحه‌نمایش", label: "وقتی زمان صفحه‌نمایش تمام می‌شود، عصبانی می‌شود یا مقاومت می‌کند." },
      { id: "q54", type: F, concern: "ترجیح صفحه‌نمایش به بازی", label: "صفحه‌نمایش را به بازی و آدم‌ها ترجیح می‌دهد." },
      { id: "scr_scary", type: "yesno", optional: true, label: "تا حالا فیلم، بازی یا خبر ترسناک و خشن دیده است؟" },
    ],
  },
  {
    title: "ترس‌ها", icon: "🌙",
    desc: "تا قصه‌ها دقیقاً همان ترس را آرام کنند.",
    questions: [
      { id: "fear_gate", type: "fearlist", label: "آیا {name} از چیز مشخصی می‌ترسد؟", desc: "هر ترس را جدا انتخاب کنید و شدتش را بگویید (۱ کم، ۴ خیلی زیاد)." },
      { id: "fear_other", type: "text", label: "ترس دیگری که در فهرست نبود؟", maxLen: 200, required: false, when: (a) => a.fear_gate === true },
      { id: "q60", type: "text", label: "وقتی می‌ترسد چه می‌کند و معمولاً چه چیزی آرامش می‌کند؟", maxLen: 500, required: false, when: (a) => a.fear_gate === true },
      { id: "par_scare", type: "single", label: "آیا در خانه گاهی برای حرف‌گوش‌کردن، او را با چیزی (لولو، پلیس، آمپول، دکتر) می‌ترسانند؟",
        desc: "فقط برای راهنمایی شماست و وارد قصه نمی‌شود.", options: ["هرگز", "گاهی", "اغلب"] },
    ],
  },
  {
    title: "سلامت و رشد", icon: "🧩",
    desc: "اختیاری و غیرتشخیصی. پرسش‌های رشدی فقط برای راهنمایی شما هستند و وارد قصه نمی‌شوند.",
    questions: [
      { id: "hlt_dx", type: "multi", optional: true, exclusive: "هیچ‌کدام", label: "آیا تشخیص یا شرایط خاصی دارد؟",
        options: ["هیچ‌کدام", "بیش‌فعالی/نقص توجه", "طیف اوتیسم", "تأخیر گفتار", "لکنت", "اختلال یادگیری", "بیماری مزمن (آسم، دیابت، صرع…)", "عینک یا سمعک", "معلولیت جسمی", "ترجیح می‌دهم نگویم"] },
      { id: "hlt_therapy", type: "yesno", optional: true, label: "الان با روان‌شناس، گفتاردرمان یا کاردرمان کار می‌کند؟" },
      { id: "hlt_show", type: "yesno", optional: true, label: "دوست دارید قهرمان قصه هم مثل او (مثلاً عینک یا اسپری آسم) داشته باشد؟",
        when: (a) => (a.hlt_dx || []).some(x => ["بیماری مزمن (آسم، دیابت، صرع…)", "عینک یا سمعک", "معلولیت جسمی"].includes(x)) },
      { id: "_h_dev", type: "header", label: "چند نشانهٔ رشدی" },
      { id: "q45", type: F, dev: true, label: "تغییر برنامهٔ روزانه خیلی ناراحتش می‌کند." },
      { id: "q46", type: F, dev: true, label: "حرکات تکراری دارد (دست زدن، تاب خوردن، چیدن اشیا به ترتیب خاص)." },
      { id: "q47", type: F, dev: true, label: "به صدا، نور، لمس یا بافت خاصی خیلی حساس است." },
      { id: "q48", type: F, dev: true, ages: [3, 5], label: "کمتر نگاه می‌کند یا کمتر با اشاره چیزی را نشان می‌دهد." },
      { id: "q49", type: F, dev: true, label: "متوجه ناراحتی یا احساس دیگران نمی‌شود." },
      { id: "q50", type: F, dev: true, ages: [3, 6], label: "در جمله‌سازی یا گفتن منظورش از هم‌سن‌ها عقب‌تر است." },
      { id: "q51", type: F, dev: true, ages: [3, 5], label: "کلمه‌ها یا جمله‌های شنیده‌شده را عیناً و پشت‌سرهم تکرار می‌کند." },
      { id: "q52", type: F, dev: true, ages: [3, 5], label: "کمتر بازی وانمودی می‌کند (مثلاً وانمود کند عروسک غذا می‌خورد)." },
      { id: "dev_tic", type: F, dev: true, ages: [5, 10], label: "پلک زدن، صدا یا حرکت تکراریِ ناخواسته (تیک) دارد." },
      { id: "_devnote", type: "devnote" },
    ],
  },
  {
    title: "شما و {name}", icon: "🫶",
    desc: "فقط برای راهنمایی شماست و وارد قصه نمی‌شود.",
    questions: [
      { id: "par_hard", type: "multi", exclusive: "هیچ‌کدام", label: "سخت‌ترین لحظه‌های روزتان با او",
        options: ["صبح و آماده شدن", "غذا", "وقت خواب", "تکلیف", "خاموش کردن صفحه‌نمایش", "دعوای بچه‌ها", "مهمانی و بیرون از خانه", "هیچ‌کدام"] },
      { id: "q66", type: F, scale: "parent", optional: true, label: "وقتی رفتار نادرستی دارد، آرامشتان را حفظ می‌کنید." },
      { id: "q67", type: F, scale: "parent", optional: true, label: "قانون‌های خانه روشن و ثابت‌اند." },
      { id: "q68", type: F, scale: "parent", optional: true, label: "تشویقش می‌کنید احساسش را با کلمه بگوید." },
      { id: "q69", type: F, scale: "parent", optional: true, label: "کار خوبش را همان لحظه و مشخص تحسین می‌کنید." },
      { id: "q70", type: F, scale: "parent", optional: true, label: "وقتی خودتان عصبانی‌اید، جلوی او بلند می‌گویید چطور آرام می‌شوید." },
      { id: "q71", type: F, scale: "parent", optional: true, label: "سعی می‌کنید او را از هر کار سختی دور نگه دارید." },
      { id: "par_react", type: "multi", optional: true, label: "وقتی رفتار نادرستی دارد، معمولاً چه می‌کنید؟",
        options: ["گفت‌وگو", "محروم کردن از چیزی", "کمی مکث تا آرام شود", "نادیده گرفتن", "داد زدن", "تهدید", "تنبیه بدنی"] },
    ],
  },
  {
    title: "قصه‌ها و ارزش‌ها", icon: "⭐",
    desc: "ارزش‌ها در پسِ قصه می‌آیند، نه به شکل نصیحت.",
    questions: [
      { id: "q74", type: "multi", max: 3, label: "مهم‌ترین ارزش‌هایی که دوست دارید در قصه‌ها باشد (حداکثر ۳)",
        options: ["صداقت", "مهربانی و همدلی", "شکرگزاری", "قناعت", "احترام به پدر و مادر", "ادب", "صبر و خودکنترلی", "شجاعت", "پشتکار", "مسئولیت‌پذیری", "نظم و پاکیزگی", "بخشش", "همکاری", "مهربانی با حیوانات و طبیعت", "توکل و دعا", "امید"] },
      { id: "q73", type: "multi", max: 2, label: "کدام ویژگی قهرمان‌ها برایش جذاب‌تر است؟ (حداکثر ۲)",
        options: ["شجاعت و قدرت جسمانی (مثل یک ابرقهرمان)", "مهربانی و کمک به دیگران (مثل یک دوست دلسوز)", "هوش و توانایی حل معما (مثل یک محقق)", "پشتکار و توانایی غلبه بر شکست", "توانایی‌های جادویی یا ویژه (خارج از دنیای واقعی)"] },
      { id: "int_include", type: "yesno", optional: true, label: "نام خواهر/برادر یا حیوان خانگی در قصه‌ها بیاید؟" },
    ],
  },
  {
    title: "اولویت‌ها", icon: "🎯",
    desc: "قصه‌های اول دربارهٔ موضوع اول‌اند؛ بقیه در ماه‌های بعد می‌آیند.",
    questions: [
      { id: "q_priority", type: "rank", max: 3, label: "کدام موضوع‌ها از همه مهم‌ترند؟ (تا ۳ مورد، به ترتیب)" },
    ],
  },
];

// Every story-topic label, by id — the frequency items' `concern`, the fears,
// the sibling chips, sleeping alone and the life events. Mirrors
// services/questionnaire_v3.py CONCERN_LABELS on the server.
export const CONCERN_LABELS = (() => {
  const m = {};
  for (const sec of Q_SECTIONS)
    for (const q of sec.questions)
      if (q.concern) m[q.id] = q.concern;
  for (const f of FEAR_ITEMS) m[f.id] = f.concern;
  for (const s of Object.values(SIBLING_FLAGS)) m[s.id] = s.label;
  m[SLEEP_ALONE_CONCERN.id] = SLEEP_ALONE_CONCERN.label;
  for (const e of LIFE_EVENTS) m[e.id] = e.concern;
  return m;
})();
const FREQ_CONCERN_IDS = Q_SECTIONS.flatMap(s => s.questions).filter(q => q.concern && q.type === F).map(q => q.id);
export const DEV_FLAG_IDS = Q_SECTIONS.flatMap(s => s.questions).filter(q => q.dev).map(q => q.id);

// The story topics the parent's answers raise, most-severe first — the same rule
// as the server's flagged() (services/questionnaire_v3.py).
export function flaggedConcerns(answers) {
  const out = [];
  for (const id of FREQ_CONCERN_IDS) {
    const v = answers[id];
    if (typeof v === "number" && v >= 3) out.push({ id, label: CONCERN_LABELS[id], severity: v });
  }
  for (const f of FEAR_ITEMS) {
    const v = answers[f.id];
    if (v && typeof v === "object" && v.has) out.push({ id: f.id, label: f.concern, severity: v.severity || 1 });
  }
  for (const chip of answers.fam_sib_rel || []) {
    const s = SIBLING_FLAGS[chip];
    if (s) out.push({ id: s.id, label: s.label, severity: 3 });
  }
  if (answers.slp_goal === true) out.push({ ...SLEEP_ALONE_CONCERN, severity: 3 });
  for (const [id, ev] of Object.entries(answers.life_events || {})) {
    const e = LIFE_EVENTS.find(x => x.id === id);
    if (e && ev && (ev.impact || 0) >= 2) out.push({ id, label: e.concern, severity: ev.impact + 1 });
  }
  return out.sort((a, b) => b.severity - a.severity);
}

// What the «اولویت‌ها» rank offers: every flagged topic, then the values the
// parent chose (a strength to grow is a fine spine for a story too).
export function priorityCandidates(answers) {
  const seen = new Set();
  const out = [];
  for (const f of flaggedConcerns(answers)) if (!seen.has(f.label)) { seen.add(f.label); out.push(f); }
  for (const v of answers.q74 || []) {
    const label = `پرورش «${v}»`;
    if (!seen.has(label)) { seen.add(label); out.push({ id: `val:${v}`, label, severity: 0 }); }
  }
  return out;
}

export const FREQ_OPTIONS = [
  { label: "هرگز", value: 1 },
  { label: "گاهی", value: 2 },
  { label: "اغلب", value: 3 },
  { label: "تقریباً همیشه", value: 4 },
];
// «نمی‌دانم» is stored as 0 — answered, never a story topic.
export const DONT_KNOW = { label: "نمی‌دانم", value: 0 };
export const PARENT_FREQ_OPTIONS = [
  { label: "هرگز", value: 1 },
  { label: "گاهی", value: 2 },
  { label: "اغلب", value: 3 },
  { label: "همیشه", value: 4 },
];

// Is `q` shown for this child, given the answers so far?
export function isVisible(q, answers, age) {
  if (q.ages && age && (age < q.ages[0] || age > q.ages[1])) return false;
  if (q.when && !q.when(answers)) return false;
  return true;
}
